'use client'

import type {
  GlobalOptions as ConfettiGlobalOptions,
  CreateTypes as ConfettiInstance,
  Options as ConfettiOptions,
} from 'canvas-confetti'
import confetti from 'canvas-confetti'
import type { ReactNode } from 'react'
import React, {
  createContext,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'

import { Button, ButtonProps } from './Button'

type Api = {
  fire: (options?: ConfettiOptions) => void
}

type Props = React.ComponentPropsWithRef<'canvas'> & {
  options?: ConfettiOptions
  globalOptions?: ConfettiGlobalOptions
  manualstart?: boolean
  children?: ReactNode
}

export type ConfettiRef = Api | null

const ConfettiContext = createContext<Api>({} as Api)

// Define component first
const ConfettiComponent = forwardRef<ConfettiRef, Props>((props, ref) => {
  console.log('ConfettiComponent mounted')
  const {
    options,
    globalOptions = { resize: true, useWorker: true },
    manualstart = false,
    children,
    ...rest
  } = props
  const instanceRef = useRef<ConfettiInstance | null>(null)

  const canvasRef = useCallback(
    (node: HTMLCanvasElement) => {
      if (node !== null) {
        if (instanceRef.current) return
        instanceRef.current = confetti.create(node, {
          ...globalOptions,
          resize: true,
        })
      } else {
        if (instanceRef.current) {
          instanceRef.current.reset()
          instanceRef.current = null
        }
      }
    },
    [globalOptions],
  )

  const fire = useCallback(
    async (opts = {}) => {
      try {
        console.log('firing confetti', { ...options, ...opts })
        await instanceRef.current?.({ ...options, ...opts })
      } catch (error) {
        console.error('Confetti error:', error)
      }
    },
    [options],
  )

  const api = useMemo(
    () => ({
      fire,
    }),
    [fire],
  )

  useImperativeHandle(ref, () => api, [api])

  useEffect(() => {
    if (!manualstart) {
      ;(async () => {
        try {
          await fire()
        } catch (error) {
          console.error('Confetti effect error:', error)
        }
      })()
    }
  }, [manualstart, fire])

  return (
    <ConfettiContext.Provider value={api}>
      <canvas ref={canvasRef} {...rest} />
      {children}
    </ConfettiContext.Provider>
  )
})

// Set display name immediately
ConfettiComponent.displayName = 'Confetti'

// Export as Confetti
export const Confetti = ConfettiComponent

interface ConfettiButtonProps extends ButtonProps {
  options?: ConfettiOptions &
    ConfettiGlobalOptions & { canvas?: HTMLCanvasElement }
  children?: React.ReactNode
}

const ConfettiButtonComponent = ({
  options,
  children,
  ...props
}: ConfettiButtonProps) => {
  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      const rect = event.currentTarget.getBoundingClientRect()
      const x = rect.left + rect.width / 2
      const y = rect.top + rect.height / 2
      await confetti({
        ...options,
        origin: {
          x: x / window.innerWidth,
          y: y / window.innerHeight,
        },
      })
    } catch (error) {
      console.error('Confetti button error:', error)
    }
  }

  return (
    <Button onClick={handleClick} {...props}>
      {children}
    </Button>
  )
}

ConfettiButtonComponent.displayName = 'ConfettiButton'

export const useConfettiSideCannons = (options: {
  particleCount?: number
  angle?: number
  spread?: number
  startVelocity?: number
  parentElement?: HTMLElement
  parentContainerId?: string
}) => {
  const trigger = useCallback(() => {
    const end = Date.now() + 3 * 1000 // 3 seconds
    const colors = ['#a786ff', '#fd8bbc', '#eca184', '#f8deb1']

    const parentContainer = document.getElementById(
      options.parentContainerId || 'flow-engine-container',
    )

    //take canvas from parent container or create a new one
    const canvas =
      parentContainer.querySelector('canvas') ||
      document.createElement('canvas')

    canvas.width = parentContainer.clientWidth
    canvas.height = parentContainer.clientHeight
    canvas.style.zIndex = '999999999'
    canvas.style.top = '0'
    canvas.style.position = 'fixed'

    parentContainer.appendChild(canvas)
    const instance = confetti.create(canvas)

    const frame = () => {
      if (Date.now() > end) return

      instance({
        particleCount: 2,
        angle: 60,
        spread: 55,
        startVelocity: 60,
        origin: { x: 0, y: 0.5 },
        colors: colors,
        ...(options || {}),
      })

      instance({
        particleCount: 2,
        angle: 120,
        spread: 55,
        startVelocity: 60,
        origin: { x: 1, y: 0.5 },
        colors: colors,
        ...(options || {}),
      })

      requestAnimationFrame(frame)
    }

    frame()
  }, [])

  return {
    trigger,
  }
}

export const ConfettiButton = ConfettiButtonComponent
